<!-- 
HACCP情報ユーザーの一覧画面
@props
  itemList : ユーザー一覧として表示するユーザー情報の配列
  canAddUser : ユーザーを追加できる状態であればtrue、そうでなければfalseを指定する
-->

<template>
  <div>
    <div class="ml-2 text-h5">グループ管理</div>
    <v-data-table
      locale="jp"
      mobile-breakpoint="0"
      v-model="selected"
      :headers="headers"
      :items="itemList"
      :custom-sort="sortFunction"
      item-key="groupId"
      show-select
      hide-default-footer
      :single-select="false"
      :search="searchCondition"
      :page.sync="page"
      :items-per-page="itemsPerPage"
      @page-count="pageCount = $event"
      no-results-text="検索条件に合うデータがありません"
      no-data-text="データがありません"
    >
      <!-- 表ヘッダ部のボタン -->
      <template v-slot:top>
        <v-btn
          color="primary"
          class="ma-2"
          @click="create"
          :disabled="!canAddGroup"
        >
          グループ追加
        </v-btn>

        <v-menu bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              class="ma-2"
              v-bind="attrs"
              v-on="on"
              :disabled="!selectionStatus.includes('contain.any')"
            >
              操作 <v-icon>mdi-chevron-down</v-icon></v-btn
            >
          </template>
          <v-list>
            <v-list-item
              v-for="(item, index) in items"
              :key="index"
              :disabled="!selectionStatus.includes(item.condition)"
              @click="item.event"
            >
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <!-- ヘッダの定義 -->
      <template v-slot:[`header.data-table-select`]="{ props, on }">
        <v-simple-checkbox
          :value="props.value || props.indeterminate"
          v-on="on"
          :indeterminate="props.indeterminate"
          dark
          :ripple="false"
        />
      </template>
      <template
        v-slot:[`item.data-table-select`]="{ item, isSelected, select }"
      >
        <v-simple-checkbox
          v-if="item.groupEdit.editable"
          :value="isSelected"
          @input="select($event)"
          :ripple="false"
        >
        </v-simple-checkbox>
      </template>
      <template v-slot:[`header.admin`]="{}">
        <all-open-button
          :title="headers[2].text"
          @on-change-open-state="onChangeAdminState"
        ></all-open-button>
      </template>
      <template v-slot:[`header.user`]="{}">
        <all-open-button
          :title="headers[3].text"
          @on-change-open-state="onChangeUserState"
        ></all-open-button>
      </template>
      <!-- テーブルのコンテンツの定義 -->
      <template v-slot:[`item.groupName`]="{ item }">
        <td class="fixed-width-300 text-truncate">{{ item.groupName }}</td>
      </template>
      <template v-slot:[`item.parentGroup`]="{ item }">
        <td class="fixed-width-300 text-truncate">{{ item.parentGroup }}</td>
      </template>
      <template v-slot:[`item.admin`]="{ item }">
        <tooltips
          :ref="'adminTooltips.' + item.groupId"
          :data="item.admin"
          :max="3"
          class="fixed-width-300"
        ></tooltips>
      </template>
      <template v-slot:[`item.user`]="{ item }">
        <tooltips
          :ref="'userTooltips.' + item.groupId"
          :data="item.user"
          :max="3"
          class="fixed-width-300"
        ></tooltips>
      </template>
      <template v-slot:[`item.groupEdit`]="{ item }">
        <v-chip
          color="primary"
          dark
          label
          v-if="item.groupEdit.editable"
          @click.stop="edit(item.groupId)"
        >
          編集
        </v-chip>
      </template>
    </v-data-table>
    <div class="text-center pt-2">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="7"
      ></v-pagination>
    </div>
    <!-- 確認ダイアログ -->
    <confirm-dialog ref="confirm_group_delete" @on-click-ok="deleteGroup">
      グループを削除します。よろしいですか？
    </confirm-dialog>
    <confirm-dialog
      ref="confirm_attach_permission"
      :autoClose="false"
      @on-click-ok="attachPermision"
    >
      選択したグループへの権限を、メールアドレスのユーザーに付与します。よろしいですか？
      <v-container class="mt-4">
        <v-row>
          <v-col cols="4"><v-subheader>メールアドレス</v-subheader></v-col>
          <v-col cols="8"
            ><v-text-field
              dense
              outlined
              v-model.trim="$v.permission_mail_address.$model"
              label="メールアドレス"
              hide-details="auto"
              :error-messages="permissionEmailErrors"
              :error-count="3"
            ></v-text-field
          ></v-col>
        </v-row>
        <v-row>
          <v-col cols="4"><v-subheader>ユーザー権限</v-subheader></v-col>
          <v-col cols="8"
            ><v-select
              :items="permissions"
              v-model="permission_selected"
              dense
              hide-details
              outlined
            ></v-select
          ></v-col>
        </v-row>
      </v-container>
    </confirm-dialog>
  </div>
</template>

<script>
import ConfirmDialog from "../dialog/confirm.vue";
import Tooltips from "../common/tooltips.vue";
import AllOpenButton from "../common/all-open-button.vue";
import Roles from "@/logic/common/roles.js";
import Validate from "@/logic/common/validate.js";
import { required, maxLength } from "vuelidate/lib/validators";
import valueConverter from "../../logic/common/value-converter";
export default {
  props: ["itemList", "canAddGroup"],
  components: {
    "confirm-dialog": ConfirmDialog,
    tooltips: Tooltips,
    "all-open-button": AllOpenButton,
  },
  /**
   * 入力チェック
   */
  validations: {
    // 権限付与のダイアログ：メールアドレス
    permission_mail_address: {
      required,
      email: Validate.rule.email,
      maxLength: maxLength(128),
      halfCharacter: Validate.rule.halfCharacter,
    },
  },
  computed: {
    /**
     * チェックボックスの選択状態
     */
    selectionStatus() {
      if (this.selectedGroupList.length == 0) {
        return [];
      }
      return ["contain.any"];
    },
    /**
     * チェックボックスで選択したアカウントの一覧
     * ※this.selectedは全選択の時に選択不可のアイテムも返すため、選択不可のアイテムを除いたものを参照する
     */
    selectedGroupList() {
      return this.selected.filter((item) => item.groupEdit.editable);
    },
    /**
     * 権限付与のダイアログ：メールアドレス入力チェックのエラー
     */
    permissionEmailErrors() {
      return Validate.buildLocalizedErrorMessage(
        this.$v.permission_mail_address,
        "メールアドレス",
        [
          "required",
          "maxLength",
          "halfCharacter",
          { value: "email", condition: this.delayCheck },
        ],
        !this.delayCheck
      );
    },
  },
  data() {
    return {
      // 検索条件（入力中）
      search: "",
      // 検索条件（一覧反映中）
      searchCondition: "",
      // 表示中のページ番号
      page: 1,
      // ページ数
      pageCount: 0,
      // ページあたりに表示するユーザー件数
      itemsPerPage: 20,
      // 選択中のユーザー情報
      selected: [],
      // 一覧のヘッダ定義
      headers: [
        {
          text: "グループ名",
          align: "start",
          value: "groupName",
          class: "grey darken-1 white--text",
          width: "300px",
        },
        {
          text: "親グループ",
          align: "start",
          value: "parentGroup",
          class: "grey darken-1 white--text",
          width: "300px",
        },
        {
          text: "所属グループ管理者",
          align: "start",
          value: "admin",
          class: "grey darken-1 white--text pl-30",
          sortable: false,
          width: "50%",
        },
        {
          text: "所属閲覧者",
          align: "start",
          value: "user",
          class: "grey darken-1 white--text pl-30",
          sortable: false,
          width: "50%",
        },
        {
          text: "グループ編集",
          align: "start",
          value: "groupEdit",
          class: "grey darken-1 white--text",
          sortable: false,
          width: "130px",
        },
      ],
      // 権限付与ダイアログ：遅延チェック
      // 送信押下時にtrueにする
      // trueになっている場合に限り、一部の入力チェックを実施する
      delayCheck: false,
      // 権限付与ダイアログ：メールアドレス
      permission_mail_address: "",
      // 権限付与ダイアログ：ロール
      permissions: Roles.linkRoles(),
      permission_selected: Roles.linkRoles()[0].value,
      // 操作ボタンを押した時に表示するメニューの一覧
      items: [
        {
          title: "グループ削除",
          condition: "contain.any",
          event: () => {
            // グループ削除ダイアログを表示する
            this.$refs.confirm_group_delete.showDialog();
          },
        },
        {
          title: "権限付与",
          condition: "contain.any",
          event: () => {
            // 権限付与ダイアログの表示データを初期化する
            this.delayCheck = false;
            this.permission_mail_address = "";
            this.$v.permission_mail_address.$reset();
            this.permission_selected = Roles.linkRoles()[0].value;
            // 権限付与ダイアログを表示する
            this.$refs.confirm_attach_permission.showDialog();
          },
        },
      ],
    };
  },
  methods: {
    /**
     * 並び替えをカスタマイズする
     */
    sortFunction(items, sortBy, sortDesc) {
      if (sortBy.length == 0) {
        const userGroupIds = this.$store.getters.userGroupIds;
        return valueConverter.dataTableCustomSort(
          items,
          ["groupName"],
          [false],
          (a, b) => {
            // ログインユーザーの所属グループと同じIDであれば先頭に表示する
            if (userGroupIds.includes(a.groupId)) {
              return -1;
            }
            if (userGroupIds.includes(b.groupId)) {
              return 1;
            }
            // ログインユーザー以外のユーザーはデフォルトの比較方法で判定を行う
            return undefined;
          }
        );
      }
      return valueConverter.dataTableDefaultSort(items, sortBy, sortDesc);
    },
    /**
     * グループを編集する
     */
    edit(group_id) {
      this.$router.push(`/group/edit/${group_id}`);
    },
    /**
     * グループを新規作成する
     */
    create() {
      this.$router.push("/group/edit/create");
    },
    /**
     * 所属グループ管理者の全て開くボタン（全て開く/全て閉じる）が押下された
     */
    onChangeAdminState(state) {
      Object.keys(this.$refs)
        .filter((key) => key.startsWith("adminTooltips."))
        .forEach((key) => {
          const item = this.$refs[key];
          if (item) {
            item.setEllipsisState(!state);
          }
        });
    },
    /**
     * 所属閲覧者の全て開くボタン（全て開く/全て閉じる）が押下された
     */
    onChangeUserState(state) {
      Object.keys(this.$refs)
        .filter((key) => key.startsWith("userTooltips."))
        .forEach((key) => {
          const item = this.$refs[key];
          if (item) {
            item.setEllipsisState(!state);
          }
        });
    },
    /**
     * グループを削除する
     */
    deleteGroup() {
      this.$emit("on-delete-group", this.selectedGroupList);
      this.selected = [];
    },
    /**
     * 権限を付与する
     */
    attachPermision() {
      // 遅延チェックフラグを立てる
      // 遅延チェック（メールアドレス形式の検証）を有効にする
      this.delayCheck = true;
      if (this.$v.permission_mail_address.$invalid) {
        // もし入力に検証エラーがあればダイアログを閉じない
        return;
      }

      this.$emit(
        "on-attach-permission",
        this.selectedGroupList,
        this.permission_mail_address,
        this.permission_selected
      );
      this.selected = [];

      //ダイアログを閉じる
      this.$refs.confirm_attach_permission.hideDialog();
    },
  },
};
</script>

<style>
th {
  background-color: #757575 !important;
}
th.sortable .v-data-table-header__icon {
  color: rgb(255, 255, 255, 0.15) !important;
}
th.sortable.active .v-data-table-header__icon {
  color: rgb(255, 255, 255, 1) !important;
}
</style>
